<script>
    import { onMount } from "svelte";

    import {
        user,
        bupaPractices,
        postOpSurveys,
        preOpQuestionnaires,
    } from "../../stores";

    import {
        getPaymentStatus,
        getDoctorStatus,
        getBillingStatus,
        getUserStatus,
        getProviderBupaStatus,
        getProviderHcfStatus,
        getProviderNibStatus,
        showError,
        showException,
        getQuoteStatus,
        getEncounterStatus,
        getHandOverType,
        isDateAndTimeInPast,
        isObjectEmpty,
        isDateInPast,
        isDateInPastOrToday,
    } from "../../Utils/utilFunctions.svelte";

    import {
        paymentStatus,
        doctorStatus,
        BillingStatus,
        UserStatus,
        providerBupaStatus,
        providerHcfStatus,
        providerNibStatus,
        QuoteStatuses,
        encounterStatus,
        handOverType,
        DirectPaymentStatus,
        DirectEncounterStatus,
        DirectDoctorStatus,
        DirectUserStatuses,
    } from "../../Utils/utilEnums.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        ADMIN_EMAIL_CAN_EDIT_STATUS,
        ADMIN_ID_CAN_EDIT_STATUS,
        checkPatientPaidPaymentsViaChangeEncounterStatus,
    } from "../../Utils/utilConstants.svelte";

    import { NotificationModal } from "./index.svelte";

    import { getFilteredPreOpInstructions } from "../../Utils/utilCache.svelte";

    import { isEnvNewOrApp } from "../../Utils/utilRoute.svelte";

    export let row = {},
        col = {},
        onFinish = () => {},
        type,
        containerStyle = "",
        textStyle = "";

    let isUpdateStatusModalOpen = false,
        isUpdatingStatus = false,
        isEditEnabled = false,
        itemId,
        itemStatus,
        backUpStatus,
        targetStatus;

    let filteredPreOpInstructions = [];

    let encounterLessThan72Hours = false,
        showConfirmChangePreOpQuestionnaireWithEncounterLessThan72Hours = false;

    let showCancelEncounterWithPatientPaymentModal = false,
        cancelEncounterSkipRefund = false;

    $: if (type == "patientEncounterSkipQuote")
        backUpStatus = row.skipQuote.toString() == "true" ? 1 : 2;

    $: if (type == "patientEncounterPatientClaim")
        backUpStatus = row.patientClaim.toString() == "true" ? 1 : 2;

    const typeEnableEditOnNewAndApp = [
        "insurance_funds",
        "all_providers",
        "hospitals",
    ];

    onMount(async () => {
        if (row) {
            switch (type) {
                case "payment":
                    itemId = row.payments[0].id;
                    itemStatus = row.payments[0].status;
                    break;
                case "paymentFromView":
                    itemId = row.id;
                    itemStatus = row.status;
                    break;
                case "patientEncounterStatus":
                    itemId = row.id;
                    itemStatus = row.status;
                    break;
                case "patientEncounterBillingStatus":
                case "patientQuote":
                    itemId = row.id;
                    itemStatus = row.billingStatus;
                    break;
                case "patientEncounterHandOverType":
                    itemId = row.id;
                    itemStatus = row.handOverType;
                    break;
                case "patientEncounterSkipQuote":
                    itemId = row.id;
                    itemStatus = row.skipQuote.toString() == "true" ? 1 : 2;
                    break;
                case "patientEncounterPatientClaim":
                    itemId = row.id;
                    itemStatus = row.patientClaim.toString() == "true" ? 1 : 2;
                    break;
                case "doctor":
                    itemId = row.id;
                    itemStatus = row.status;
                    break;
                case "user":
                case "admin":
                    itemId = row.id;
                    itemStatus = row.status;
                    break;
                case "accountProvider_BUPA":
                    itemId = row.id;
                    itemStatus = row.isNoGapBupa;
                    break;
                case "accountProvider_BUPA_PracticeID":
                    let practiceId;
                    if (row.bupaPractice)
                        practiceId = row.bupaPractice.practiceId;

                    itemId = practiceId;
                    itemStatus = practiceId;
                    break;
                case "accountProvider_HCF":
                    itemId = row.id;
                    itemStatus = row.isNoGapHCF;
                    break;
                case "accountProvider_NIB":
                    itemId = row.id;
                    itemStatus = row.isNoGapNib;
                    break;
                case "manager":
                    itemId = row.id;
                    itemStatus = row.status;
                    break;
                case "patientEncounterProviderNo":
                    const { data, selectedNumber, options } = row;
                    itemId = data.id;
                    itemStatus = options.find(
                        (option) => option.type == selectedNumber,
                    )?.id;
                    break;
                case "patientEncounterPreOpQuestionnaire":
                    itemId = row.id;
                    itemStatus = row.questionnaireId;
                    if (row.encounterScheduledDate) {
                        const diffInHours =
                            Math.abs(
                                new Date() -
                                    new Date(row.encounterScheduledDate),
                            ) /
                            (1000 * 60 * 60);

                        encounterLessThan72Hours = diffInHours < 72;
                    }
                    break;
                case "patientEncounterPreOpInstruction":
                    itemId = row.id;
                    itemStatus = row.instructionId;
                    filteredPreOpInstructions =
                        await getFilteredPreOpInstructions(row.doctor);
                    break;
                case "patientEncounterPostOpSurvey":
                    itemId = row.id;
                    itemStatus = row.surveyId;
                    break;
                case "insurance_funds":
                case "hospitals":
                    itemId = row.id;
                    itemStatus = row.isNoGapProgram ? Number(1) : Number(0);
                    break;
                case "all_providers":
                    itemId = row.uniqueId;
                    itemStatus = row.isNoGapProgram ? Number(1) : Number(0);
                    break;
                case "account_provider":
                    itemId = row.id;
                    itemStatus = row.isNoGapProgram ? Number(1) : Number(0);
                    break;
                default:
                    itemId = undefined;
                    itemStatus = undefined;
                    break;
            }
            backUpStatus = itemStatus;
        }
    });

    $: canUserEdit =
        ((row.enableDoctorEdit ? $user.role >= 2 : $user.role >= 20) &&
            type !== "admin") ||
        ((row.enableDoctorEdit ? $user.role >= 2 : $user.role >= 20) &&
            ($user.id == ADMIN_ID_CAN_EDIT_STATUS ||
                $user.email == ADMIN_EMAIL_CAN_EDIT_STATUS));

    $: canUserEditEncounterStatus =
        $user.role >= 2 && type == "patientEncounterStatus";

    $: canUserEditInsuranceFundGroupType = isEnvNewOrApp();

    const updateStatus = async () => {
        isUpdatingStatus = true;
        switch (type) {
            case "payment":
            case "paymentFromView":
                await apiMethods.putMethod
                    .updatePaymentStatus(itemId, targetStatus)
                    .then((res) => {
                        if (res.id) {
                            onFinish();
                            backUpStatus = targetStatus;
                        } else if (res.error) {
                            showError(res.error);
                        } else {
                            showError(
                                "Unable to update this payment status, please refresh the page and try again and contact support if the issue persists.",
                            );
                        }
                    })
                    .catch((err) => {
                        showException(err);
                    });
                break;

            case "patientEncounterStatus":
                if (targetStatus == 9)
                    // Delete encounter
                    await apiMethods.deleteMethod
                        .deletePatientEncounter(itemId)
                        .then((res) => {
                            if (res.id) {
                                onFinish();
                                backUpStatus = targetStatus;
                            } else
                                showError(
                                    res.error ??
                                        "Failed to delete encounter, please refresh the page and try again and contact support if the issue persists.",
                                );
                        })
                        .catch((err) => showException(err));
                else if (backUpStatus == 9 && targetStatus == 0)
                    // restore deleted encounter back to active status
                    await apiMethods.putMethod
                        .unDeletePatientEncounter(itemId)
                        .then((res) => {
                            if (res.id) {
                                onFinish();
                                backUpStatus = targetStatus;
                            } else
                                showError(
                                    res.error ??
                                        "Failed to restore encounter, please refresh the page and try again and contact support if the issue persists.",
                                );
                        })
                        .catch((err) => showException(err));
                else if (
                    showCancelEncounterWithPatientPaymentModal &&
                    cancelEncounterSkipRefund
                )
                    await apiMethods.putMethod
                        .cancelEncounterWithoutRefund(row.id)
                        .then((res) => {
                            if (res.id) {
                                onFinish();
                                backUpStatus = targetStatus;
                            } else if (res.error) showError(res.error);
                            else
                                showError(
                                    "Unable to cancel this encounter, please refresh the page and try again and contact support if the issue persists.",
                                );
                        })
                        .catch((err) => showException(err))
                        .finally(() => {
                            cancelEncounterSkipRefund = false;
                            showCancelEncounterWithPatientPaymentModal = false;
                        });
                else
                    await apiMethods.putMethod
                        .updateEncounterStatus(itemId, targetStatus)
                        .then((res) => {
                            if (res.id) {
                                onFinish();
                                backUpStatus = targetStatus;
                            } else if (res.error) showError(res.error);
                            else
                                showError(
                                    "Unable to update this encounter status, please refresh the page and try again and contact support if the issue persists.",
                                );
                        })
                        .catch((err) => showException(err));
                break;

            case "patientEncounterBillingStatus":
            case "patientQuote":
                await apiMethods.putMethod
                    .updateBillingStatus(itemId, targetStatus)
                    .then((res) => {
                        if (res.id) {
                            onFinish();
                            backUpStatus = targetStatus;
                        } else if (res.error) {
                            showError(res.error);
                        } else {
                            showError(
                                "Unable to update this billing status, please refresh the page and try again and contact support if the issue persists.",
                            );
                        }
                    })
                    .catch((err) => {
                        showException(err);
                    });
                break;

            case "patientEncounterHandOverType":
                await apiMethods.putMethod
                    .updateHandOverType(itemId, Number(targetStatus))
                    .then((res) => {
                        if (res.id) {
                            onFinish();
                            backUpStatus = targetStatus;
                        } else
                            showError(
                                res.error ??
                                    "Failed to update hand-over status, please refresh the page and try again and contact support if the issue persists.",
                            );
                    })
                    .catch((err) => showException(err));
                break;

            case "doctor":
                await apiMethods.putMethod
                    .updateDoctorStatus(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) {
                            showError(res.error);
                        } else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => {
                        showException(err);
                    });
                break;

            case "user":
            case "admin":
                await apiMethods.putMethod
                    .updateUserStatus(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) {
                            showError(res.error);
                        } else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => {
                        showException(err);
                    });
                break;

            case "accountProvider_BUPA":
                row.isNoGapBupa = targetStatus.toString() == "true";

                await apiMethods.putMethod
                    .updateAccountProvider(row)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "account_provider":
                row.isNoGapProgram = targetStatus == 1;

                await apiMethods.putMethod
                    .updateAccountProvider(row)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "accountProvider_BUPA_PracticeID":
                row.bupaPracticeId = $bupaPractices.find(
                    (bp) => bp.practiceId == targetStatus,
                )?.id;

                await apiMethods.putMethod
                    .updateAccountProvider(row)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "accountProvider_HCF":
                row.isNoGapHCF = targetStatus.toString() == "true";

                await apiMethods.putMethod
                    .updateAccountProvider(row)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "accountProvider_NIB":
                row.isNoGapNib = targetStatus.toString() == "true";

                await apiMethods.putMethod
                    .updateAccountProvider(row)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "manager":
                await apiMethods.putMethod
                    .updateUserStatus(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "patientEncounterProviderNo":
                await apiMethods.putMethod
                    .updateEncounterProviderNumber(
                        row.data.id,
                        Number(targetStatus),
                    )
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = Number(targetStatus);
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "patientEncounterPreOpQuestionnaire":
                await apiMethods.putMethod
                    .updateEncounterPreOpQuestionnaire(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "patientEncounterPreOpInstruction":
                await apiMethods.putMethod
                    .updateEncounterPreOpInstruction(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "patientEncounterPostOpSurvey":
                await apiMethods.putMethod
                    .updateEncounterPostOpSurvey(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    })
                    .catch((err) => showException(err));
                break;

            case "insurance_funds":
                await apiMethods.putMethod
                    .updateInsuranceFundIsGapProgram(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    });
                break;

            case "all_providers":
                await apiMethods.putMethod
                    .updateProviderIsGapProgram(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    });
                break;

            case "hospitals":
                await apiMethods.putMethod
                    .updateHospitalIsGapProgram(itemId, targetStatus)
                    .then((res) => {
                        if (res.error) showError(res.error);
                        else {
                            onFinish();
                            backUpStatus = targetStatus;
                        }
                    });
                break;

            default:
                break;
        }

        isUpdatingStatus = false;
        isUpdateStatusModalOpen = false;
        showConfirmChangePreOpQuestionnaireWithEncounterLessThan72Hours = false;

        itemStatus = isNaN(targetStatus) ? targetStatus : Number(targetStatus);
    };

    const enableEdit = () => {
        if (!canUserEdit && !canUserEditEncounterStatus) return;

        if (
            typeEnableEditOnNewAndApp.includes(type) &&
            !canUserEditInsuranceFundGroupType
        )
            return;

        isEditEnabled = true;

        const options = getStatusOptions();

        if (options.length == 1 && options[0].id) {
            if (itemStatus && itemStatus == options[0].id) return;
            else {
                itemStatus = options[0].id;
                askUpdateStatus(itemStatus);
            }
        }
    };

    const cancelEdit = () => {
        isEditEnabled = false;
        itemStatus = backUpStatus;
    };

    $: getDisplayStatus = (v) => {
        switch (type) {
            case "payment":
            case "paymentFromView":
                return v > -1 ? getPaymentStatus(v) : "-";
            case "patientEncounterStatus":
                return v > -1 ? getEncounterStatus(v) : "-";
            case "patientEncounterBillingStatus":
                return v > -1 ? getBillingStatus(v) : "-";
            case "patientEncounterHandOverType":
                return v > -1 ? getHandOverType(v, "dropdownType") : "-";
            case "patientQuote":
                return v > -1 ? getQuoteStatus(v) : "-";
            case "patientEncounterSkipQuote":
            case "patientEncounterPatientClaim":
            case "insurance_funds":
            case "all_providers":
            case "hospitals":
                return v == 1 ? "Yes" : "No";
            case "account_provider":
                return v == 1 ? "No Gap" : "Known Gap";
            case "doctor":
                return v > -1 ? getDoctorStatus(v) : "-";
            case "user":
            case "admin":
            case "manager":
                return v > -1 ? getUserStatus(v) : "-";
            case "accountProvider_BUPA":
                return v > -1 ? getProviderBupaStatus(v) : "-";
            case "accountProvider_BUPA_PracticeID":
                const firstDefaultBupaPractice = $bupaPractices
                    .filter((bp) => bp.groupId == row.accountGroupId)
                    .find((practice) => practice.isDefault);
                const bupaPractice = $bupaPractices
                    .filter((bp) => bp.groupId == row.accountGroupId)
                    .find((practice) => practice.practiceId == v);

                return bupaPractice
                    ? `${v} (${bupaPractice.state})`
                    : firstDefaultBupaPractice
                      ? `${firstDefaultBupaPractice.practiceId} (${firstDefaultBupaPractice.state})`
                      : "-";
            case "accountProvider_HCF":
                return v > -1 ? getProviderHcfStatus(v) : "-";
            case "accountProvider_NIB":
                return v > -1 ? getProviderNibStatus(v) : "-";
            case "patientEncounterProviderNo":
                return v > -1 ? row.selectedNumber : "-";
            case "patientEncounterPreOpQuestionnaire":
                return (
                    $preOpQuestionnaires.find(
                        (questionnaire) => questionnaire.id == v,
                    )?.name ?? "None"
                );
            case "patientEncounterPreOpInstruction":
                return (
                    filteredPreOpInstructions.find(
                        (instruction) => instruction.id == v,
                    )?.name ?? "None"
                );
            case "patientEncounterPostOpSurvey":
                return (
                    $postOpSurveys.find((survey) => survey.id == v)?.name ??
                    "None"
                );
            default:
                return "-";
        }
    };

    const getStatusOptions = () => {
        switch (type) {
            case "payment":
            case "paymentFromView":
                return _getPaymentStatusOptions(backUpStatus);
            case "patientEncounterStatus":
                return encounterStatus;
            case "patientEncounterBillingStatus":
                // Billing status Submitted, In-Progress and Completed are only available if scheduled date is today or in the past
                return !row?.scheduledDate ||
                    !isDateInPastOrToday(row.scheduledDate)
                    ? BillingStatus.filter(({ id }) => id < 3)
                    : BillingStatus;
            case "patientEncounterHandOverType":
                return handOverType.map((type) => ({
                    ...type,
                    type: type.dropdownType,
                }));
            case "patientQuote":
                return QuoteStatuses;
            case "patientEncounterSkipQuote":
            case "patientEncounterPatientClaim":
                return [
                    {
                        id: 1,
                        type: "Yes",
                    },
                    {
                        id: 2,
                        type: "No",
                    },
                ];
            case "doctor":
                return doctorStatus;
            case "user":
            case "admin":
            case "manager":
                return UserStatus;
            case "accountProvider_BUPA":
                return providerBupaStatus;
            case "accountProvider_BUPA_PracticeID":
                const bupaPracticeOptions = [];

                $bupaPractices.map((practice) => {
                    const { id, practiceId, state, groupId } = practice;

                    if (groupId == row.accountGroupId)
                        bupaPracticeOptions.push({
                            id: practiceId,
                            type: `${practiceId} (${state})`,
                            sort: id,
                        });
                });

                return bupaPracticeOptions.sort((a, b) => a.sort - b.sort);
            case "accountProvider_HCF":
                return providerHcfStatus;
            case "accountProvider_NIB":
                return providerNibStatus;
            case "patientEncounterProviderNo":
                return row.options;
            case "patientEncounterPreOpQuestionnaire":
                const preOpQuestionnaireOptions = [{ id: 0, type: "None" }];

                $preOpQuestionnaires.map((questionnaire) => {
                    preOpQuestionnaireOptions.push({
                        id: questionnaire.id,
                        type: questionnaire.name,
                    });
                });

                return preOpQuestionnaireOptions;
            case "patientEncounterPreOpInstruction":
                const preOpInstructionOptions = [{ id: 0, type: "None" }];

                filteredPreOpInstructions.map((instruction) => {
                    preOpInstructionOptions.push({
                        id: instruction.id,
                        type: instruction.name,
                    });
                });

                return preOpInstructionOptions;
            case "patientEncounterPostOpSurvey":
                const postOpSurveyOptions = [{ id: 0, type: "None" }];

                $postOpSurveys.map((survey) => {
                    postOpSurveyOptions.push({
                        id: survey.id,
                        type: survey.name,
                    });
                });

                return postOpSurveyOptions;

            case "insurance_funds":
            case "all_providers":
            case "hospitals":
                return [
                    {
                        id: 1,
                        type: "Yes",
                    },
                    {
                        id: 0,
                        type: "No",
                    },
                ];

            case "account_provider":
                return [
                    {
                        id: 1,
                        type: "No Gap",
                    },
                    {
                        id: 0,
                        type: "Known Gap",
                    },
                ];
            default:
                return [];
        }
    };

    const askUpdateStatus = async (value) => {
        targetStatus = value;

        const skipConfirmProcess = [
            "patientEncounterSkipQuote",
            "patientEncounterPatientClaim",
        ];

        if (skipConfirmProcess.includes(type)) {
            backUpStatus = targetStatus;
            onFinish(targetStatus);
            return;
        } else if (
            type == "patientEncounterPreOpQuestionnaire" &&
            encounterLessThan72Hours
        )
            showConfirmChangePreOpQuestionnaireWithEncounterLessThan72Hours = true;
        else {
            isRedActionButton = getActionButtonType();

            if (
                type == "patientEncounterStatus" &&
                checkPatientPaidPaymentsViaChangeEncounterStatus.includes(
                    Number(targetStatus),
                )
            )
                await _handleCancelEncounterWithPatientPayments();
            else isUpdateStatusModalOpen = true;
        }
    };

    $: getStatusChangePrompt = () => {
        switch (type) {
            case "payment":
            case "paymentFromView":
                return `Change the payment status to ${getPaymentStatus(
                    targetStatus,
                )}?`;
            case "patientEncounterStatus":
                return showCancelEncounterWithPatientPaymentModal
                    ? "Change encounter status to Cancelled and refund patient?"
                    : `Change the encounter status to ${getEncounterStatus(targetStatus)}`;
            case "patientEncounterBillingStatus":
                return `Change the billing status to ${getBillingStatus(
                    targetStatus,
                )}?`;
            case "patientEncounterHandOverType":
                return `Change the hand-over status to ${getHandOverType(targetStatus, "dropdownType")}?`;
            case "patientQuote":
                return `Change the fee estimate status to ${getQuoteStatus(
                    targetStatus,
                )}?`;
            case "doctor":
                return `Change the status of this doctor to ${getDoctorStatus(
                    targetStatus,
                )}?`;
            case "user":
                return `Change the status of this user to ${getUserStatus(
                    targetStatus,
                )}?`;
            case "admin":
                return `Change the status of this Admin to ${getUserStatus(
                    targetStatus,
                )}?`;
            case "accountProvider_BUPA":
                return `Change the BUPA status of this provider to ${getProviderBupaStatus(
                    targetStatus,
                )}?`;
            case "accountProvider_BUPA_PracticeID":
                const targetState = $bupaPractices.find(
                    (practice) => practice.practiceId == targetStatus,
                )?.state;

                return `Change the BUPA Practice ID of this provider to ${targetStatus} (${targetState})`;
            case "accountProvider_HCF":
                return `Change the HCF status of this provider to ${getProviderHcfStatus(
                    targetStatus,
                )}?`;
            case "accountProvider_NIB":
                return `Change the NIB status of this provider to ${getProviderNibStatus(
                    targetStatus,
                )}?`;
            case "manager":
                return `Change the status of this manager to ${getUserStatus(
                    targetStatus,
                )}?`;
            case "patientEncounterProviderNo":
                const providerNo = row.options.find(
                    (option) => option.id == targetStatus,
                )?.type;
                return `Change the encounter provider No. to ${providerNo}?`;
            case "patientEncounterPreOpQuestionnaire":
                const newQuestionnaireName =
                    !targetStatus || targetStatus == 0
                        ? "None"
                        : $preOpQuestionnaires.find(
                              (questionnaire) =>
                                  questionnaire.id == targetStatus,
                          )?.name;

                return `Change the PreOp Questionnaire to ${newQuestionnaireName}?`;
            case "patientEncounterPreOpInstruction":
                const newInstructionName =
                    !targetStatus || targetStatus == 0
                        ? "None"
                        : filteredPreOpInstructions.find(
                              (instruction) => instruction.id == targetStatus,
                          )?.name;

                return `Change the PreOp Instruction to ${newInstructionName}?`;
            case "patientEncounterPostOpSurvey":
                const newSurveyName =
                    !targetStatus || targetStatus == 0
                        ? "None"
                        : $postOpSurveys.find(
                              (survey) => survey.id == targetStatus,
                          )?.name;

                return `Change the PostOp Survey to ${newSurveyName}?`;
            case "insurance_funds":
                return `Change Insurance Fund to ${targetStatus == 0 ? "NORMAL Program" : "NO-GAP Program"}?`;
            case "all_providers":
                return `Change Provider to ${targetStatus == 0 ? "NORMAL Program" : "NO-GAP Program"}?`;
            case "account_provider":
                return `Change Provider to ${targetStatus == 0 ? "KNOWN-GAP Program" : "NO-GAP Program"}?`;
            case "hospitals":
                return `Change Hospital to ${targetStatus == 0 ? "NORMAL Program" : "NO-GAP Program"}?`;
            default:
                return "";
        }
    };

    const getStatusChangeTitle = () => {
        switch (type) {
            case "payment":
            case "paymentFromView":
                return "Update Payment Status";
            case "BillingStatus":
            case "patientEncounterBillingStatus":
                return "Update Billing Status";
            case "patientEncounterHandOverType":
                return "Update Hand-Over Status";
            case "patientQuote":
                return "Update Fee Estimate Status";
            case "doctor":
                return "Update Doctor Status";
            case "user":
                return "Update User Status";
            case "admin":
                return "Update Admin Status";
            case "accountProvider_BUPA":
                return "Update BUPA Status";
            case "accountProvider_BUPA_PracticeID":
                return "Update BUPA Practice ID";
            case "accountProvider_HCF":
                return "Update HCF Status";
            case "accountProvider_NIB":
                return "Update NIB Status";
            case "manager":
                return "Update Manager Status";
            case "patientEncounterProviderNo":
                return "Update encounter provider No.";
            case "patientEncounterPreOpQuestionnaire":
                return "Update encounter PreOp Questionnaire";
            case "patientEncounterPreOpInstruction":
                return "Update encounter PreOp Instruction";
            case "patientEncounterPostOpSurvey":
                return "Update encounter PostOp Survey";
            case "patientEncounterStatus":
                return "Update Encounter Status";
            case "insurance_funds":
                return "Update Insurance Funds Gap Program";
            case "all_providers":
            case "account_provider":
                return "Update Provider Gap Program";
            case "hospitals":
                return "Update Hospital Gap Program";
            default:
                return "Update";
        }
    };

    let isRedActionButton = false;

    const getActionButtonType = () => {
        switch (type) {
            case "payment":
            case "paymentFromView":
                return targetStatus == DirectPaymentStatus.Cancelled;
            case "patientEncounterStatus":
                return targetStatus == DirectEncounterStatus.Archived;
            case "doctor":
                return (
                    targetStatus == DirectDoctorStatus.Blocked ||
                    targetStatus == DirectDoctorStatus.Deleted
                );
            case "user":
            case "admin":
            case "manager":
                return (
                    targetStatus == DirectUserStatuses.Blocked ||
                    targetStatus == DirectUserStatuses.Inactive ||
                    targetStatus == DirectUserStatuses.Deleted
                );
            case "patientEncounterPreOpQuestionnaire":
            case "patientEncounterPreOpInstruction":
            case "patientEncounterPostOpSurvey":
            case "patientEncounterHandOverType":
            case "patientEncounterBillingStatus":
            case "patientQuote":
            case "patientEncounterSkipQuote":
            case "patientEncounterPatientClaim":
            case "accountProvider_BUPA":
            case "accountProvider_BUPA_PracticeID":
            case "accountProvider_HCF":
            case "accountProvider_NIB":
            case "patientEncounterProviderNo":
            case "insurance_funds":
            case "all_providers":
            case "account_provider":
            case "hospitals":
            default:
                return false;
        }
        return false;
    };

    const _getPaymentStatusOptions = (defaultStatus) => {
        if (defaultStatus == 9) return paymentStatus;
        else if (defaultStatus == 0 || defaultStatus == 1 || defaultStatus == 2)
            return paymentStatus.filter(
                (status) =>
                    status.id == 0 ||
                    status.id == 1 ||
                    status.id == 2 ||
                    status.id == 9,
            );
        else if (defaultStatus == 4 || defaultStatus == 5)
            return paymentStatus.filter(
                (status) => status.id == 4 || status.id == 5 || status.id == 9,
            );
        else if (defaultStatus == 7)
            return paymentStatus.filter(
                (status) => status.id == 7 || status.id == 9,
            );
    };

    const _handleCancelEncounterWithPatientPayments = async () => {
        showCancelEncounterWithPatientPaymentModal = false;

        let encounterFullDetails = {};

        await apiMethods.getMethod
            .getPatientEncounterById(row.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to get encounter details, please try again later or contact support...",
                    );
                else encounterFullDetails = { ...res };
            });

        if (isObjectEmpty(encounterFullDetails)) isUpdateStatusModalOpen = true;
        else {
            const { estimates, invoices } = encounterFullDetails;

            let stopCheck = false;

            if (!!estimates.length)
                estimates.map((est) => {
                    if (
                        est.payments.length &&
                        est.payments.some(
                            ({ paidBy, status }) =>
                                paidBy == 0 && [1, 2].includes(status),
                        )
                    ) {
                        showCancelEncounterWithPatientPaymentModal = true;
                        stopCheck = true;
                    }
                });

            if (!stopCheck && !!invoices.length)
                invoices.map((inv) => {
                    if (
                        inv.payments.length &&
                        inv.payments.some(
                            ({ paidBy, status }) =>
                                paidBy == 0 && [1, 2].includes(status),
                        )
                    ) {
                        showCancelEncounterWithPatientPaymentModal = true;
                        stopCheck = true;
                    }
                });

            isUpdateStatusModalOpen = true;
        }
    };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="flex flex-row items-center justify-start {containerStyle}"
    on:click={enableEdit}
    on:focusout={cancelEdit}
    on:blur={cancelEdit}
>
    {#if isEditEnabled}
        <select
            class="input input-sm w-auto input-bordered"
            bind:value={itemStatus}
            on:change={(e) => askUpdateStatus(e.target.value)}
        >
            {#each getStatusOptions() as op}
                <option value={op.id}> {op.type}</option>
            {/each}
        </select>
    {:else}
        <span
            class="text-left {textStyle} {canUserEdit ||
            canUserEditEncounterStatus
                ? typeEnableEditOnNewAndApp.includes(type)
                    ? canUserEditInsuranceFundGroupType
                        ? 'underline cursor-pointer'
                        : 'cursor-default'
                    : `${type == 'patientEncounterProviderNo' ? `${$user.role == 2 ? 'underline' : ''}` : 'underline'} cursor-pointer`
                : 'cursor-default'}"
        >
            {getDisplayStatus(backUpStatus)}
        </span>
    {/if}
</div>

{#if type == "patientEncounterBillingStatus" && row.handOver > 0}
    <p class="text-center">Handed Over</p>
{/if}

<NotificationModal
    bind:isModalOpen={isUpdateStatusModalOpen}
    bind:isConfirming={isUpdatingStatus}
    title={getStatusChangeTitle()}
    submitText="Confirm"
    cancelText="Cancel"
    modalMaxWidth={showCancelEncounterWithPatientPaymentModal ? "max-w-xl" : ""}
    confirmButtonClass={isRedActionButton ? "btn-error" : "btn-secondary"}
    confirmAction={updateStatus}
    cancelButtonClass="btn-accent"
    closeModal={() => {
        isUpdateStatusModalOpen = false;
        showCancelEncounterWithPatientPaymentModal = false;
    }}
    closeModalFunction={() => {
        isUpdateStatusModalOpen = false;
        showCancelEncounterWithPatientPaymentModal = false;
    }}
    showCancelBtn={!showCancelEncounterWithPatientPaymentModal}
    showConfirmBtn={!showCancelEncounterWithPatientPaymentModal}
    btnOptions={[
        {
            text: "Cancel",
            class: "btn-accent w-auto",
            loading: isUpdatingStatus,
            isHidden: !showCancelEncounterWithPatientPaymentModal,
            function: () => {
                isUpdateStatusModalOpen = false;
                showCancelEncounterWithPatientPaymentModal = false;
            },
        },
        {
            text: isUpdatingStatus ? "Confirming" : "Confirm, No Refund",
            class: "btn-secondary text-xs w-auto",
            loading: isUpdatingStatus,
            isHidden: !showCancelEncounterWithPatientPaymentModal,
            function: () => {
                cancelEncounterSkipRefund = true;
                updateStatus();
            },
        },
        {
            text: isUpdatingStatus ? "Confirming" : "Confirm and Refund",
            class: "btn-secondary text-xs w-auto",
            loading: isUpdatingStatus,
            isHidden: !showCancelEncounterWithPatientPaymentModal,
            function: () => updateStatus(),
        },
    ]}
>
    <p class="text-left">
        {getStatusChangePrompt()}
    </p>
</NotificationModal>

<NotificationModal
    isModalOpen={showConfirmChangePreOpQuestionnaireWithEncounterLessThan72Hours}
    title="Pre-Op Questionnaire Changed"
    cancelText="Cancel"
    submitText="Set and Send Now"
    cancelButtonClass="btn-error"
    closeModal={() => {
        showConfirmChangePreOpQuestionnaireWithEncounterLessThan72Hours = false;
        targetStatus = backUpStatus;
    }}
    confirmAction={() => updateStatus()}
    closeModalFunction={() => {
        showConfirmChangePreOpQuestionnaireWithEncounterLessThan72Hours = false;
        targetStatus = backUpStatus;
    }}
>
    <div class="flex flex-col gap-y-4">
        <p class="leading-6">
            This encounter is scheduled to less than 72 hours from now.
        </p>
        <p class="leading-6">
            Setting or changing the Pre-Op Questionnaire now will send an
            immediate notification to the patient to complete it.
        </p>
        <p class="leading-6">Do you want to continue?</p>
    </div>
</NotificationModal>
